import { useEffect, useState } from "react";
import { Button, Card as AntCard, Descriptions, Empty, Form, Input, Modal, Popconfirm, Select, Switch, message } from "antd";
import { useTranslation } from "react-i18next";
import { useManagementStore } from "@/stores/management";
import { orgService, type RoleInfo, type SsoConfigInfo } from "@/services/organization";

export function SsoConfig() {
  const { t } = useTranslation("management");
  const { context } = useManagementStore();
  const orgId = context?.type === "org" ? context.org.id : "";
  const [config, setConfig] = useState<SsoConfigInfo | null>(null);
  const [roles, setRoles] = useState<RoleInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [form] = Form.useForm();

  const load = async () => {
    if (!orgId) return;
    setLoading(true);
    try {
      const [c, r] = await Promise.all([orgService.getSso(orgId), orgService.listRoles(orgId)]);
      setConfig(c);
      setRoles(r);
    } catch { /* empty */ }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [orgId]);

  const openModal = () => {
    if (config) {
      form.setFieldsValue({
        provider_name: config.provider_name,
        issuer_url: config.issuer_url,
        client_id: config.client_id,
        scopes: config.scopes.join(", "),
        default_role_id: config.default_role_id,
        account_bind_field: config.account_bind_field,
        auto_create_user: config.auto_create_user,
        enabled: config.enabled,
      });
    } else {
      form.resetFields();
      form.setFieldsValue({ scopes: "openid, profile, email", account_bind_field: "email", auto_create_user: true });
    }
    setModalOpen(true);
  };

  const handleSubmit = async () => {
    const values = await form.validateFields();
    const data = {
      ...values,
      scopes: values.scopes.split(",").map((s: string) => s.trim()).filter(Boolean),
    };
    try {
      if (config) {
        await orgService.updateSso(orgId, data);
      } else {
        await orgService.createSso(orgId, data);
      }
      message.success(t("common:success"));
      setModalOpen(false);
      load();
    } catch { message.error(t("common:error")); }
  };

  const handleDelete = async () => {
    try {
      await orgService.deleteSso(orgId);
      message.success(t("common:success"));
      setConfig(null);
    } catch { message.error(t("common:error")); }
  };

  if (loading) return null;

  return (
    <>
      {config ? (
        <AntCard>
          <Descriptions column={1} size="small">
            <Descriptions.Item label={t("sso.provider_name")}>{config.provider_name}</Descriptions.Item>
            <Descriptions.Item label={t("sso.issuer_url")}>{config.issuer_url}</Descriptions.Item>
            <Descriptions.Item label={t("sso.client_id")}>{config.client_id}</Descriptions.Item>
            <Descriptions.Item label={t("sso.scopes")}>{config.scopes.join(", ")}</Descriptions.Item>
            <Descriptions.Item label={t("sso.bind_field")}>{config.account_bind_field}</Descriptions.Item>
            <Descriptions.Item label={t("sso.auto_create")}>{config.auto_create_user ? "Yes" : "No"}</Descriptions.Item>
            <Descriptions.Item label={t("sso.enabled")}>{config.enabled ? "Yes" : "No"}</Descriptions.Item>
          </Descriptions>
          <div style={{ marginTop: 16, display: "flex", gap: 8 }}>
            <Button type="primary" onClick={openModal}>{t("common:edit")}</Button>
            <Popconfirm title={t("sso.delete.confirm")} onConfirm={handleDelete}>
              <Button danger>{t("common:delete")}</Button>
            </Popconfirm>
          </div>
        </AntCard>
      ) : (
        <Empty description={t("sso.not_configured")}>
          <Button type="primary" onClick={openModal}>{t("sso.create")}</Button>
        </Empty>
      )}

      <Modal title={t("sso.title")} open={modalOpen} onOk={handleSubmit} onCancel={() => setModalOpen(false)} width={520}>
        <Form form={form} layout="vertical">
          <Form.Item label={t("sso.provider_name")} name="provider_name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item label={t("sso.issuer_url")} name="issuer_url" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item label={t("sso.client_id")} name="client_id" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item label={t("sso.client_secret")} name="client_secret" rules={config ? [] : [{ required: true }]}>
            <Input.Password placeholder={config ? "(unchanged)" : ""} />
          </Form.Item>
          <Form.Item label={t("sso.scopes")} name="scopes">
            <Input />
          </Form.Item>
          <Form.Item label={t("sso.default_role")} name="default_role_id" rules={[{ required: true }]}>
            <Select options={roles.map((r) => ({ label: r.name, value: r.id }))} />
          </Form.Item>
          <Form.Item label={t("sso.bind_field")} name="account_bind_field" rules={[{ required: true }]}>
            <Select options={[
              { label: "email", value: "email" },
              { label: "sub", value: "sub" },
              { label: "preferred_username", value: "preferred_username" },
            ]} />
          </Form.Item>
          <Form.Item label={t("sso.auto_create")} name="auto_create_user" valuePropName="checked">
            <Switch />
          </Form.Item>
          {config && (
            <Form.Item label={t("sso.enabled")} name="enabled" valuePropName="checked">
              <Switch />
            </Form.Item>
          )}
        </Form>
      </Modal>
    </>
  );
}
